import Link from 'components/ui/AppLink';
import ArrowsChevronSmallLeft from '@mds/icons/CaretLeft';
import { Box, Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Checkbox } from 'components/ui/FormControls';
import React, { useState, useEffect } from 'react';
import debounce from 'lodash/debounce';

import { SUBSCRIPTIONS } from '../../../gql/subscriptions';
import SectionHeader from '../../ui/SectionHeader';
import {
    ChannelSubscription,
    EmailNotificationSettings,
} from '../../../shared/types';
import { useMutation } from '@apollo/react-hooks';
import CardNoContentHeading from '../../ui/card/CardNoContentHeading';
import Card from '../../ui/card/Card';
import { useActivityLogger } from '../../hooks/use-activity-logger';
import { baseUrls } from '../../../utils/baseUrls.utils';
import PageHeader from '../../ui/PageHeader';
import {
    GET_USER_EMAIL_NOTIFICATION_SETTINGS,
    SET_USER_EMAIL_NOTIFICATION_SETTINGS,
    SET_PREMIUM_CHANNELS_EMAIL_NOTIFICATION_SETTINGS,
    UserSubscriptionsResponse,
} from '../../../gql/users';

import { useQuery } from '@apollo/react-hooks';
import Loader from 'components/ui/LoadingIndicator';
enum EnableStatus {
    NoAction,
    Enable,
    Disable,
}

export type EmailNotifications = 'channelNotifications';

const ManageEmailNotificationsPage = () => {
    const pageLoadComplete = useActivityLogger(
        'EmailNotifications',
        'Email notifications',
        undefined,
        true
    );

    const [changingSubscription, setChangingSubscription] =
        useState<EnableStatus>(EnableStatus.NoAction);

    const querySubscriptions = useQuery<UserSubscriptionsResponse>(
        SUBSCRIPTIONS,
        { fetchPolicy: 'network-only' }
    );
    const { data, loading } = useQuery(GET_USER_EMAIL_NOTIFICATION_SETTINGS, {
        fetchPolicy: 'network-only',
    });
    const [setEmailNotifications] = useMutation(
        SET_USER_EMAIL_NOTIFICATION_SETTINGS
    );
    const [setPremiumChannelsEmailNotifications] = useMutation(
        SET_PREMIUM_CHANNELS_EMAIL_NOTIFICATION_SETTINGS,
        {
            refetchQueries: [{ query: SUBSCRIPTIONS }],
        }
    );

    const [emailNotifications, setEmailNotification] =
        useState<EmailNotificationSettings | null>(null);

    const debounceSetEmailNotifications = debounce(
        (data) =>
            setEmailNotifications({
                variables: {
                    item: { ...data },
                },
            }),
        500
    );

    const subscribedChannels =
        querySubscriptions?.data?.me?.channelSubscriptions
            .filter((cs) => cs?.channel && cs?.subscribed)
            .sort((a, b) => a.channel.name.localeCompare(b.channel.name)) || [];

    useEffect(() => {
        setEmailNotification({
            channelNotifications:
                !!data?.me?.emailNotificationSettings?.channelNotifications,
        });
    }, [data]);

    useEffect(() => {
        setChangingSubscription(EnableStatus.NoAction);
    }, [changingSubscription]);

    if (querySubscriptions.loading || loading) return <Loader />;

    pageLoadComplete();

    const haveNotSubscribedToAnythingYet = !subscribedChannels.length;

    const handleToolbarButtonsClick = (
        type: EmailNotifications,
        value: boolean
    ) => {
        if (!emailNotifications) return;
        if (emailNotifications[type] === value) return;

        const formattedEmailNotifications: EmailNotificationSettings = {
            ...emailNotifications,
            [type]: value,
        };

        setEmailNotification(formattedEmailNotifications);
        debounceSetEmailNotifications(formattedEmailNotifications);
    };

    const onPremiumChannelsEmailNotificationsChange = (channelId: string) => {
        setPremiumChannelsEmailNotifications({
            variables: { channelId },
        });
    };

    const generateToolbarButtons = (type: EmailNotifications) => (
        <ToggleButtonGroup
            size="medium"
            exclusive
            value={emailNotifications?.[type] ? 'on' : 'off'}
            onChange={(_, value: 'on' | 'off' | null) => {
                if (!value) return;
                handleToolbarButtonsClick(type, value === 'on');
            }}
            aria-label="Channel email notifications"
        >
            <ToggleButton
                value="on"
                aria-label="Turn channel email notifications on"
            >
                On
            </ToggleButton>
            <ToggleButton
                value="off"
                aria-label="Turn channel email notifications off"
            >
                Off
            </ToggleButton>
        </ToggleButtonGroup>
    );

    const channelsDisabledCheckbox = !emailNotifications?.channelNotifications;

    return (
        <Grid container spacing={4}>
            <Grid size={12}>
                <Link
                    to={baseUrls.subscriptions}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        '& svg': { mr: 0.5 },
                    }}
                >
                    <ArrowsChevronSmallLeft />
                    Back to your subscriptions
                </Link>
                <PageHeader
                    title="Update your email notifications"
                    subtitle="Manage your email notifications to make Insights work better for you."
                    subtitleInk="muted"
                />
            </Grid>
            {haveNotSubscribedToAnythingYet ? (
                <Grid size={12}>
                    <Box sx={{ pt: 0.5 }}>
                        <Card>
                            <CardNoContentHeading>
                                You haven't subscribed to anything yet.
                            </CardNoContentHeading>
                        </Card>
                    </Box>
                </Grid>
            ) : (
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                    }}
                >
                    <Grid container spacing={4}>
                        {subscribedChannels.length ? (
                            <Grid size={12}>
                                <SectionHeader
                                    id="channels-header"
                                    title="Emails from Channels"
                                    subtitle="Receive emails from Channels you subscribe to when new articles are published"
                                    headingToolbar={generateToolbarButtons(
                                        'channelNotifications'
                                    )}
                                />
                                <Grid container spacing={1}>
                                    {subscribedChannels.map(
                                        (
                                            cs: ChannelSubscription,
                                            key: number
                                        ) => (
                                            <Grid
                                                key={`channel_${key}`}
                                                size={12}
                                            >
                                                <Checkbox
                                                    id="premium-channels-checkbox"
                                                    label={cs.channel.name}
                                                    value="a"
                                                    checked={
                                                        cs.emailNotifications
                                                    }
                                                    onChange={() =>
                                                        onPremiumChannelsEmailNotificationsChange(
                                                            cs.channel._id
                                                        )
                                                    }
                                                    disabled={
                                                        channelsDisabledCheckbox
                                                    }
                                                />
                                            </Grid>
                                        )
                                    )}
                                </Grid>
                            </Grid>
                        ) : null}
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

export default ManageEmailNotificationsPage;
